import { QueryKey, useQuery } from "@tanstack/react-query";
import { useReducer, useState } from "react";

import { delay, delayForIndexing } from "@examspark/common-util";
import {
  AuthorizationContextInput,
  QuerySearchArgs,
  Resource,
  SearchableConnection,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  SearchableResourceSortableFields,
  SearchableSortDirection,
  SearchAggsInput,
} from "@examspark/types";

import { defaultSearchState, searchReducer, SearchState } from "./context";
import { usePaginatedSearch } from "./usePaginatedSearch";
import { buildAggs } from "./utils/buildAggs";
import { buildFilter } from "./utils/buildFilter";
import { getGenericQuery } from "./utils/buildQuery";
import { parseSearchResults } from "./utils/parseSearchResults";

type Params = {
  label?: string;
  fields?: string;
};

export const searchGqlQuery = (params?: Params) => /* GraphQL */ `
  query ${params?.label || "Search"}(
    $filter: SearchableResourceFilterInput
    $sort: [SearchableResourceSortInput!]
    $aggsNew: [SearchAggsInput!]
    $limit: Int
    $nextToken: String
    $authorizationContext: AuthorizationContextInput
  ) {
    search(
      filter: $filter
      sort: $sort
      aggsNew: $aggsNew
      limit: $limit
      nextToken: $nextToken
      authorizationContext: $authorizationContext
    ) {
      items {
        ${params?.fields ?? ` id sk name idInc createdAt updatedAt `}
      }
      aggregations
      nextToken
      total
      __typename
    }
  }
`;

export type UseSearchProps = {
  queryKey: QueryKey | string;
  /** The filter that gets applied to all queries, no matter what. */
  baseFilter?: SearchableResourceFilterInput;
  /**
   * This prop allows you to have certain filter UIs auto populate with
   * preferred defaults. This is not the same as the `baseFilter`.
   */
  initialFilter?: SearchState["filter"];
  onBeforeSearch?: (
    state: SearchState,
    filter: SearchableResourceFilterInput,
  ) => SearchableResourceFilterInput;
  customAggs?: SearchAggsInput[];
  userPrefs?: {
    objectiveAuthorityIds?: string[];
  };
  sort?: QuerySearchArgs["sort"];
  authorizationContext: AuthorizationContextInput | undefined;

  // todo: type
  gqlQuery: any | undefined;
};

// This method currently has way too much hard-coded logic for items.
// TODO: generalize
export const useSearch = (props: UseSearchProps) => {
  const keepPreviousData = true;
  const [isFetchingDelayed, setIsFetchingDelayed] = useState(false);
  const authContext = props.authorizationContext;
  const [searchState, dispatchSearch] = useReducer(searchReducer, {
    ...defaultSearchState,
    filter: props.initialFilter ?? {},
  });
  const gqlQuery = props.gqlQuery;

  const objectiveAuthorityIds =
    (props.userPrefs?.objectiveAuthorityIds as string[]) ?? [];

  const prepareQueryArgs = (
    queryContext: {
      meta: any;
    },
    params?: {
      aggregate?: boolean;
    },
  ): QuerySearchArgs => {
    const { meta } = queryContext;
    let baseFilter: SearchableResourceFilterInput = {};

    if (params?.aggregate) {
      baseFilter = { ...props.baseFilter };
      // console.log("facet filter", printJSON(filter));
      if (!baseFilter.and) baseFilter.and = [];
      if (searchState?.filter.query) {
        baseFilter.and.push(...getGenericQuery(searchState.filter.query));
      }
    } else {
      baseFilter = buildFilter({
        baseFilter: props.baseFilter,
        filter: searchState.filter,
      });
    }

    const filter = props.onBeforeSearch
      ? props.onBeforeSearch(searchState, baseFilter)
      : baseFilter;

    let aggsNew: SearchAggsInput[] = [];
    if (params?.aggregate) {
      /** `aggs` to send through appsync */
      const aggs = [...defaultAggsNew, ...(props.customAggs ?? [])];
      aggsNew = buildAggs({
        searchState,
        aggs,
        objectiveAuthorityIds,
      });
    }

    return {
      filter,
      sort: [
        ...(props.sort ? props.sort : []),
        {
          field: SearchableResourceSortableFields.Name,
          direction: SearchableSortDirection.Asc,
        },
      ],
      aggsNew,
      authorizationContext: authContext,
      ...meta?.variables,
    };
  };

  const queryKeys = Array.isArray(props.queryKey)
    ? props.queryKey
    : [props.queryKey];

  // Where the results are stored
  const query = usePaginatedSearch({
    keepPreviousData,
    queryKey: [...queryKeys, searchState.filter],
    enabled: !!authContext?.workspaceId,
    queryFn: async ({ meta }) => {
      const queryArgs = prepareQueryArgs({ meta });

      const results = await gqlQuery?.({
        query: searchGqlQuery({
          label: "SearchItems",
          fields: itemsGql,
        }),
        variables: {
          ...queryArgs,
        },
      });

      const parsed = parseSearchResults(results.data?.search);
      // console.log("results", parsed);
      return parsed as SearchableConnection<Resource>;
    },
  });

  // Where the facets are stored
  const facets = useQuery({
    keepPreviousData,
    queryKey: [props.queryKey, "facets", searchState.filter],
    enabled: !!authContext?.workspaceId,
    queryFn: async ({ meta }) => {
      const queryArgs = prepareQueryArgs(
        { meta },
        {
          aggregate: true,
        },
      );

      // @ts-expect-error foo
      const results = await gqlQuery<{
        search: SearchableResourceConnectionNew;
      }>({
        query: searchGqlQuery({
          label: "SearchItemsFacets",
        }),
        variables: {
          ...queryArgs,
          limit: 0,
        },
      });

      const parsed = parseSearchResults(results.data?.search);
      // console.log("results", parsed);
      return parsed;
    },
  });

  return {
    state: searchState,
    dispatch: dispatchSearch,
    query,
    facets,
    isFetchingDelayed,
    isFetching: query.isFetching || facets.isFetching || isFetchingDelayed,
    refetch: async (options?: { delay?: boolean | number }) => {
      if (options?.delay === true) {
        setIsFetchingDelayed(true);
        await delayForIndexing();
      } else if (options?.delay) {
        setIsFetchingDelayed(true);
        await delay(options.delay);
      }
      const result = await Promise.all([query.refetch(), facets.refetch()]);
      setIsFetchingDelayed(false);
      return result;
    },
  };
};

export const itemsGql = `
  id
  idInc
  idVendor
  sk
  type
  version
  bankId
  name
  description
  deliveryMediaType
  author
  maturity
  subject
  grade
  dok
  language
  blooms
  excerpt
  interaction
  dueAt
  editorId
  creatorId
  questionIds
  contextIds
  schemaVersion
  createdAt
  updatedAt
  workspaceId
  alignmentIds
  alignments {
    items {
      id
      sk
      idPublisher
      abbreviation
      scope
      authorityId
      content
    }
  }
  isUserFavorite
  isUserWatcher
  content {
    canvas {
      id
      type
      interactionType
      content
      config
      scoring
    }
  }
  scoring
  config
  contexts {
    items {
      id
      sk
      type
      idInc
      name
      content {
        body
      }
    }
  }
  file {
    bucket
    name
    key
    size
  }
  __typename
`;

export const DEFAULT_AGG_SIZE = 15;
const size = DEFAULT_AGG_SIZE; // default is 15, which cuts off several courses
const defaultAggsNew: SearchAggsInput[] = [
  {
    name: "subject",
    type: "terms",
    field: "subject",
    size,
  },
  {
    name: "bankId",
    type: "terms",
    field: "bankId",
    size,
  },
  {
    name: "sk",
    type: "terms",
    field: "sk",
    size,
  },
  {
    name: "deliveryMediaType",
    type: "terms",
    field: "deliveryMediaType",
    size,
  },
  {
    name: "language",
    type: "terms",
    field: "language",
    size,
  },
  {
    name: "grade",
    type: "terms",
    field: "grade",
    size,
  },
  {
    name: "interaction",
    type: "terms",
    field: "interaction",
    size,
  },
  {
    name: "objectiveScope",
    type: "terms",
    field: "objectiveScope",
    size: 100,
  },
  {
    name: "dok",
    type: "terms",
    field: "dok",
    size,
  },
  {
    name: "type",
    type: "terms",
    field: "type",
    size,
  },
  {
    name: "blooms",
    type: "terms",
    field: "blooms",
    size,
  },
  {
    name: "maturity",
    type: "terms",
    field: "maturity",
    size,
  },
  // {
  //   name: "objectiveAbbreviation",
  //   type: "terms",
  //   field: "objectiveAbbreviation",
  //   size,
  // },
  {
    name: "alignmentIds",
    type: "terms",
    field: "alignmentIds",
    size: 100,
  },
  {
    name: "referencesIdsUnit",
    type: "terms",
    field: "referencesIds.id",
    size: 100,
  },
  {
    name: "workspaceId",
    type: "terms",
    field: "workspaceId",
    size,
  },
];
